// clinicalListSplit.js — TEST 2.07. ONE shared splitter for the three sites in
// DeliverScreen.jsx that were each carrying their own `split(/[.;•\n]+/)`, which
// treats a clinical dose or temperature exactly like a sentence boundary:
// "fever above 101.5°F" became two bullets, one of them the bare fragment
// "5°F or signs", and "Dr. Smith said rest" became "Dr" and "Smith said rest".
//
// splitClinicalList(text) is deliberately a single, import-free function so the
// three call sites (and any future one) share one rule instead of three copies
// that can drift. It:
//   - always splits on newline, "•" and ";";
//   - splits on "." only when the "." is followed by whitespace and then an
//     uppercase letter, or when the "." is the last character of the text;
//   - never splits a "." that sits between two digits, or that is immediately
//     (allowing intervening whitespace) followed by "°" — a dose or a
//     temperature is not a sentence boundary;
//   - never splits a "." that closes one of a fixed list of abbreviations
//     (Dr, Mr, Mrs, Ms, St, vs, etc, e.g, i.e, approx, No), matched as a whole
//     word so it cannot fire inside an unrelated word ending the same letters.
// Trims every piece and drops empty ones. No new user-facing string: the three
// call sites keep their own `.filter(s => s.length > N)`.

const ABBREVIATIONS = ['Dr', 'Mr', 'Mrs', 'Ms', 'St', 'vs', 'etc', 'e.g', 'i.e', 'approx', 'No']

function endsWithAbbreviation(current) {
  for (const abbr of ABBREVIATIONS) {
    if (current.length < abbr.length) continue
    if (current.slice(current.length - abbr.length) !== abbr) continue
    const before = current.length - abbr.length - 1
    const boundary = before >= 0 ? current[before] : ' '
    if (!/[A-Za-z0-9]/.test(boundary)) return true
  }
  return false
}

export function splitClinicalList(text) {
  if (text == null) return []
  const str = String(text)
  const items = []
  let current = ''

  for (let i = 0; i < str.length; i++) {
    const ch = str[i]

    if (ch === '\n' || ch === '•' || ch === ';') {
      items.push(current)
      current = ''
      continue
    }

    if (ch === '.') {
      const prevChar = current.length > 0 ? current[current.length - 1] : ''
      const nextChar = i + 1 < str.length ? str[i + 1] : ''

      // Never split a "." between two digits, or one immediately (through
      // whitespace) followed by a degree sign — a dose or a temperature.
      if (/[0-9]/.test(prevChar)) {
        let k = i + 1
        while (k < str.length && /\s/.test(str[k])) k++
        const ahead = k < str.length ? str[k] : ''
        if (/[0-9]/.test(nextChar) || ahead === '°') {
          current += ch
          continue
        }
      }

      const atEnd = i === str.length - 1
      let splitHere = atEnd

      if (!splitHere) {
        let j = i + 1
        let sawWhitespace = false
        while (j < str.length && /\s/.test(str[j])) { sawWhitespace = true; j++ }
        if (sawWhitespace && j < str.length && /[A-Z]/.test(str[j])) splitHere = true
      }

      if (splitHere && endsWithAbbreviation(current)) splitHere = false

      if (splitHere) {
        // The "." is a boundary here, exactly like "\n", "•" and ";" above:
        // it is consumed, not kept as part of either piece.
        items.push(current)
        current = ''
      } else {
        current += ch
      }
      continue
    }

    current += ch
  }
  items.push(current)

  return items.map((s) => s.trim()).filter((s) => s.length > 0)
}
